import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, Play, CheckCircle, TrendingUp, Shield,
  Zap, Globe, Database, Code, Layers, ChevronRight,
  Users, Quote, Phone, IndianRupee, GraduationCap, Hotel,
  ShoppingBag, Wrench, FileText, Smartphone, MessageCircle
} from 'lucide-react';
import CountUp from 'react-countup';
import { useInView } from 'react-intersection-observer';
import SEO, { ORG_SCHEMA } from '../components/SEO';
import './Home.css';
import { FaWhatsapp } from 'react-icons/fa';
import { FaPhone } from 'react-icons/fa6';

const TRUSTED_BY = [
  'Religare & Associates', 'IWP Academy', 'Maashitla', 'Green Land Model School',
  'Valorelle', 'Sports University of Haryana', 'SKI', 'Sparepedia',
  'Indus', 'Mindex', 'Abhipra',
];

const SERVICES = [
  {
    icon: <Code size={24} />,
    name: 'Software & App Development',
    desc: 'Custom web and mobile applications built with modern stacks for performance, scale, and great user experience.',
    tag: 'Development',
  },
  {
    icon: <Database size={24} />,
    name: 'RTA Software',
    desc: 'End-to-end Registrar & Transfer Agent platform — shareholder records, dividend distribution, SEBI-compliant.',
    tag: 'Finance',
  },
  {
    icon: <GraduationCap size={24} />,
    name: 'School Management ERP',
    desc: 'Admissions, academics, fees, parent portals and real-time analytics in one connected platform.',
    tag: 'Education',
  },
  {
    icon: <Hotel size={24} />,
    name: 'Hotel Management Software',
    desc: 'Reservations, housekeeping, billing and revenue optimization for boutique hotels to large chains.',
    tag: 'Hospitality',
  },
  {
    icon: <ShoppingBag size={24} />,
    name: 'E-Commerce Platform',
    desc: 'Scalable online stores with multi-vendor support, payments, inventory and customer engagement tools.',
    tag: 'Retail',
  },
  {
    icon: <TrendingUp size={24} />,
    name: 'CRM & ERP Suite',
    desc: 'Centralized pipelines, HR, finance and inventory management — all in one enterprise-grade suite.',
    tag: 'Enterprise',
  },
  {
    icon: <Wrench size={24} />,
    name: 'Scaffolding Solutions',
    desc: 'Precision-engineered scaffolding and material management for construction & industrial projects.',
    tag: 'Construction',
  },
  {
    icon: <FileText size={24} />,
    name: 'Documentation Services',
    desc: 'Professional digitization, compliance documentation and audit-ready document workflows.',
    tag: 'Services',
  },
  {
    icon: <Smartphone size={24} />,
    name: 'Organization Setup',
    desc: 'Structured setup of your technology and processes so your organization can scale with clarity and control.',
    tag: 'Consulting',
  },
];

const FEATURES = [
  { icon: <Shield size={22} />, title: 'Enterprise Security', desc: 'SEBI, GDPR & OSHA compliant. Role-based access, encryption at rest & in transit, detailed audit trails.' },
  { icon: <Zap size={22} />, title: 'Blazing Performance', desc: 'Cloud-native architecture built to handle millions of transactions with sub-100ms response times.' },
  { icon: <Layers size={22} />, title: 'Modular & Flexible', desc: 'Pick only what you need. Customize workflows, reports, and UI without vendor lock-in.' },
  { icon: <Globe size={22} />, title: 'RESTful APIs', desc: 'Open API ecosystem that integrates with any third-party ERP, CRM or custom application seamlessly.' },
  { icon: <TrendingUp size={22} />, title: 'Real-Time Analytics', desc: 'Interactive dashboards, custom report generation & data exports for confident decision-making.' },
  { icon: <Users size={22} />, title: '24/7 Support', desc: 'Dedicated helpdesk, onboarding programs & a partner-first approach to long-term success.' },
];

const STATS = [
  { value: 27, suffix: '+', label: 'Years of Excellence' },
  { value: 50, suffix: '+', label: 'Clients Served' },
  { value: 10, suffix: '+', label: 'Software Products' },
  { value: 99, suffix: '%', label: 'Uptime SLA' },
];

const PRICING = [
  {
    name: 'Starter',
    price: '25,000',
    period: '/ project',
    desc: 'For small businesses launching their first digital product.',
    features: ['1 core module', 'Basic customization', 'Email support', '3 months free maintenance', 'Standard deployment'],
    cta: 'Get Started',
    highlight: false,
  },
  {
    name: 'Growth',
    price: '85,000',
    period: '/ project',
    desc: 'For growing businesses that need integrated, scalable systems.',
    features: ['Up to 3 modules', 'Full customization', 'Priority support', '12 months free maintenance', 'API integrations', 'Staff training included'],
    cta: 'Start Growing',
    highlight: true,
  },
  {
    name: 'Enterprise',
    price: 'Custom',
    period: 'quote',
    desc: 'For large organizations needing bespoke, multi-system platforms.',
    features: ['Unlimited modules', 'Dedicated dev team', '24/7 priority support', 'SLA guarantee', 'On-premise or cloud', 'Quarterly business reviews'],
    cta: 'Contact Sales',
    highlight: false,
  },
];

const TESTIMONIALS = [
  { name: 'Rajesh Kumar', role: 'CEO, Mindex Technologies', text: 'SHIVKMS transformed our operations completely. Their ERP reduced our manual processes by 70% within three months of deployment.' },
  { name: 'Priya Sharma', role: 'Principal, IWP Academy', text: "The School Management System is simply outstanding. Parent engagement has doubled, and admin time has halved. Couldn't be happier." },
  { name: 'Ankit Verma', role: 'GM, Indus Hotels', text: 'Our hotel occupancy reporting is now real-time. The SHIVKMS team understood our exact needs and delivered beyond expectations.' },
];

function StatItem({ value, suffix, label, delay }) {
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.5 });
  return (
    <div ref={ref} className="stat-item">
      <div className="stat-number">
        {inView ? <CountUp end={value} duration={2.5} delay={delay} /> : '0'}{suffix}
      </div>
      <div className="stat-label">{label}</div>
    </div>
  );
}

export default function Home() {
  const [activeTestimonial, setActiveTestimonial] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setActiveTestimonial(a => (a + 1) % TESTIMONIALS.length), 5000);
    return () => clearInterval(t);
  }, []);

  const { ref: featRef, inView: featInView } = useInView({ triggerOnce: true, threshold: 0.1 });

  return (
    <div className="home">
      <SEO
        title="Intelligent Software Solutions Since 1998"
        description="SHIVKMS builds enterprise-grade RTA, school management, hotel management, e-commerce, CRM & ERP software for Indian businesses. 27+ years, 50+ clients, SEBI compliant."
        keywords="software company Delhi, RTA software India, school management software, hotel management software, ERP CRM software, SHIVKMS"
        path="/"
        schema={ORG_SCHEMA}
      />

      {/* ── HERO ── */}
      <section className="hero">
        <div className="hero-bg">
          <div className="hero-orb orb-1" />
          <div className="hero-orb orb-2" />
          <div className="hero-grid" />
        </div>
        <div className="container hero-content">
          <div className="hero-text">
            <div className="badge hero-badge">
              <span className="dot" />
              Trusted since 1998 · Delhi, India
            </div>
            <h1 className="hero-headline">
              Software that
              <span className="gradient-text"> Thinks</span>
              <br />with Your Business
            </h1>
            <p className="hero-subtext">
              SHIVKMS builds modular, enterprise-grade software for finance, education, hospitality and beyond — so you can focus on what matters: growth.
            </p>
            <div className="hero-actions">
              <Link to="/products" className="btn-primary">
                Explore Services <ArrowRight size={18} />
              </Link>
              <Link to="/contact" className="btn-outline">
                Book a Demo <Play size={16} />
              </Link>
            </div>
            <div className="hero-trust">
              <div className="trust-avatars">
                {['R','M','S','A','I'].map((l,i) => (
                  <div key={i} className="trust-avatar">{l}</div>
                ))}
              </div>
              <div className="trust-text">
                <span className="trust-count">50+ companies</span> trust SHIVKMS
              </div>
            </div>
          </div>
          <div className="hero-visual">
            <div className="hero-card-stack">
              <div className="hero-card hc-main">
                <div className="hc-header">
                  <div className="hc-dot" style={{background:'#C0392B'}} />
                  <div className="hc-dot" style={{background:'#C9A84C'}} />
                  <div className="hc-dot" style={{background:'#2ECC71'}} />
                  <span className="hc-title">SHIVKMS Dashboard</span>
                </div>
                <div className="hc-body">
                  <div className="hc-metric">
                    <span className="hc-metric-label">Revenue Growth</span>
                    <span className="hc-metric-val">+42.8%</span>
                  </div>
                  <div className="hc-chart">
                    {[40,65,45,80,60,90,75].map((h,i) => (
                      <div key={i} className="hc-bar" style={{height:`${h}%`, animationDelay:`${i*0.15}s`}} />
                    ))}
                  </div>
                  <div className="hc-row">
                    <div className="hc-pill green">Uptime 99.9%</div>
                    <div className="hc-pill red">27+ Yrs</div>
                  </div>
                </div>
              </div>
              <div className="hero-card hc-floating hcf-1">
                <Shield size={18} className="hcf-icon" />
                <div>
                  <p className="hcf-title">SEBI Compliant</p>
                  <p className="hcf-sub">RTA Certified</p>
                </div>
              </div>
              <div className="hero-card hc-floating hcf-2">
                <Zap size={18} className="hcf-icon" style={{color:'#C9A84C'}} />
                <div>
                  <p className="hcf-title">10+ Products</p>
                  <p className="hcf-sub">All sectors</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="hero-scroll-indicator">
          <span>Scroll to explore</span>
          <div className="scroll-line" />
        </div>
      </section>

      {/* ── TRUSTED BY TICKER (top, matches reference site) ── */}
      <section className="trusted-by-section">
        <div className="container">
          <p className="trusted-by-label">Trusted by innovative teams</p>
        </div>
        <div className="ticker-wrap">
          <div className="ticker-track">
            {[...TRUSTED_BY, ...TRUSTED_BY, ...TRUSTED_BY].map((c, i) => (
              <span key={i} className="ticker-item">{c}</span>
            ))}
          </div>
        </div>
      </section>

      {/* ── STATS ── */}
      <section className="stats-section">
        <div className="container">
          <div className="stats-grid">
            {STATS.map((s, i) => (
              <StatItem key={i} {...s} delay={i * 0.2} />
            ))}
          </div>
        </div>
      </section>

      {/* ── EVERYTHING YOU NEED (service cards, reference-style) ── */}
      <section className="section-py products-section">
        <div className="container">
          <div className="section-heading">
            <span className="eyebrow">Our Services</span>
            <h2>Everything You <span className="gradient-text">Need</span></h2>
            <div className="divider" />
            <p>We provide end-to-end software solutions tailored to your specific business needs.</p>
          </div>
          <div className="services-grid">
            {SERVICES.map((s, i) => (
              <div key={i} className="service-card card">
                <div className="service-icon-wrap">{s.icon}</div>
                <div className="service-tag">{s.tag}</div>
                <h3 className="service-name">{s.name}</h3>
                <p className="service-desc">{s.desc}</p>
                <div className="service-actions">
                  <Link to="/contact" className="btn-primary service-btn">
                    Service details <ArrowRight size={15} />
                  </Link>
                  <Link to="/contact" className="service-btn-outline">
                    <IndianRupee size={14} /> Packages
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FEATURES ── */}
      <section className="section-py features-section" ref={featRef}>
        <div className="features-bg" />
        <div className="container">
          <div className="features-inner">
            <div className="features-left">
              <span className="eyebrow">Why SHIVKMS</span>
              <h2>Built for the <span className="gradient-text">Enterprise,<br />Accessible to All</span></h2>
              <p>Every product we ship carries the same enterprise DNA — security, performance, and long-term support — regardless of company size.</p>
              <ul className="features-checklist">
                {['SEBI, GDPR & OSHA compliant', 'Cloud & on-premises deployment', '24/7 dedicated support', 'No vendor lock-in'].map(item => (
                  <li key={item}><CheckCircle size={16} className="check-icon" />{item}</li>
                ))}
              </ul>
              <Link to="/about" className="btn-primary" style={{marginTop:'32px'}}>
                Learn About Us <ArrowRight size={18} />
              </Link>
            </div>
            <div className="features-grid-right">
              {FEATURES.map((f, i) => (
                <div key={i} className={`feature-card card ${featInView ? 'visible' : ''}`} style={{animationDelay:`${i*0.1}s`}}>
                  <div className="feature-icon">{f.icon}</div>
                  <h4 className="feature-title">{f.title}</h4>
                  <p className="feature-desc">{f.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── PRICING ── */}
      {/* <section className="section-py pricing-section">
        <div className="container">
          <div className="section-heading">
            <span className="eyebrow">Packages</span>
            <h2>Simple, <span className="gradient-text">Transparent Pricing</span></h2>
            <div className="divider" />
            <p>Choose a package that fits your stage — or talk to us for a fully custom quote.</p>
          </div>
          <div className="pricing-grid">
            {PRICING.map((p, i) => (
              <div key={i} className={`pricing-card ${p.highlight ? 'highlight' : ''}`}>
                {p.highlight && <span className="pricing-badge">Most Popular</span>}
                <h3 className="pricing-name">{p.name}</h3>
                <p className="pricing-desc">{p.desc}</p>
                <div className="pricing-price">
                  {p.price !== 'Custom' && <IndianRupee size={22} />}
                  <span className="pricing-amount">{p.price}</span>
                  <span className="pricing-period">{p.period}</span>
                </div>
                <ul className="pricing-features">
                  {p.features.map((f, j) => (
                    <li key={j}><CheckCircle size={15} className="check-icon" />{f}</li>
                  ))}
                </ul>
                <Link to="/contact" className={p.highlight ? 'btn-primary' : 'btn-outline'} style={{width:'100%', justifyContent:'center'}}>
                  {p.cta} <ArrowRight size={16} />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section> */}

      {/* ── TESTIMONIALS ── */}
      <section className="section-py testimonials-section">
        <div className="container">
          <div className="section-heading">
            <span className="eyebrow">Client Stories</span>
            <h2>What Our <span className="gradient-text">Clients Say</span></h2>
            <div className="divider" />
          </div>
          <div className="testimonials-wrapper">
            <div className="testimonial-main">
              <Quote size={36} className="quote-icon" />
              <p className="testimonial-text">{TESTIMONIALS[activeTestimonial].text}</p>
              <div className="testimonial-author">
                <div className="author-avatar">
                  {TESTIMONIALS[activeTestimonial].name[0]}
                </div>
                <div>
                  <p className="author-name">{TESTIMONIALS[activeTestimonial].name}</p>
                  <p className="author-role">{TESTIMONIALS[activeTestimonial].role}</p>
                </div>
              </div>
            </div>
            <div className="testimonial-dots">
              {TESTIMONIALS.map((_, i) => (
                <button
                  key={i}
                  className={`t-dot ${i === activeTestimonial ? 'active' : ''}`}
                  onClick={() => setActiveTestimonial(i)}
                  aria-label={`Testimonial ${i + 1}`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── CTA BANNER ── */}
      <section className="cta-banner">
        <div className="cta-banner-bg" />
        <div className="container">
          <div className="cta-banner-inner">
            <div>
              <h2 className="cta-banner-title">Ready to transform your operations?</h2>
              <p className="cta-banner-sub">Talk to our team and get a free consultation tailored to your business.</p>
            </div>
            <div className="cta-banner-actions">
              <Link to="/contact" className="btn-primary">
                Start for Free <ArrowRight size={18} />
              </Link>
              <a href="tel:+919212552928" className="cta-phone">
                <FaPhone size={18} /> +91 9212552928
              </a>
              <a href="https://wa.me/919212552928" target="_blank" rel="noopener noreferrer" className="cta-phone">
                <FaWhatsapp size={18} /> WhatsApp Us
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
